import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import {
  useAuditoria,
  useEmpresas,
  type AuditoriaLog,
} from "@/hooks/use-data";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
} from "lucide-react";
import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { fmtDateTime } from "@/lib/formatters";

// ─── constants ─────────────────────────────────────────────────────────────────

// Entidades e ações registradas pelo servidor do ReMonta (logAudit / auditoria transacional).
const ENTIDADES = [
  { value: "empresa", label: "Empresa" },
  { value: "usuario", label: "Usuário" },
  { value: "veiculo", label: "Veículo" },
  { value: "remonta_dossie_sinistro", label: "Dossiê" },
  { value: "remonta_ordem_servico", label: "Ordem de serviço" },
  { value: "remonta_dossie_arquivo", label: "Arquivo do Dossiê" },
  { value: "remonta_ocorrencia", label: "Ocorrência" },
  { value: "remonta_orcamento_item", label: "Item de orçamento" },
  { value: "remonta_laudo", label: "Laudo" },
  { value: "remonta_item_laudo", label: "Item do laudo" },
  { value: "remonta_foto", label: "Foto" },
  { value: "remonta_solicitante_permissao", label: "Permissão de Solicitante" },
];

const ACOES = [
  { value: "criar", label: "Criar" },
  { value: "atualizar", label: "Atualizar" },
  { value: "excluir", label: "Excluir" },
  { value: "redefinir_senha", label: "Redefinir senha" },
  { value: "assinatura_cadastrada", label: "Assinatura cadastrada" },
  { value: "criar_ordem", label: "Criar Ordem" },
  { value: "registrar_arquivo", label: "Registrar arquivo" },
  { value: "excluir_arquivo", label: "Excluir arquivo" },
  { value: "registrar_foto", label: "Registrar foto" },
  { value: "excluir_foto", label: "Excluir foto" },
  { value: "criar_ocorrencia", label: "Registrar ocorrência" },
  { value: "criar_item_orcamento", label: "Criar item de orçamento" },
  { value: "decidir_item_orcamento", label: "Decidir item de orçamento" },
  { value: "atualizar_nexo_causal", label: "Atualizar nexo causal" },
  { value: "decidir_auditoria", label: "Decidir auditoria" },
  { value: "validar_prontidao", label: "Validar prontidão" },
  { value: "finalizar", label: "Finalizar" },
  { value: "emitir", label: "Emitir" },
  { value: "criar_revisao", label: "Criar revisão" },
];

const ACAO_COLORS: Record<string, string> = {
  criar: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  atualizar: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  excluir: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  excluir_arquivo: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  excluir_foto: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  redefinir_senha: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  emitir: "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400",
  finalizar: "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400",
};

const ACAO_LABELS: Record<string, string> = {
  criar: "Criar",
  atualizar: "Atualizar",
  excluir: "Excluir",
  status_alterado: "Status Alterado",
  redefinir_senha: "Redefinir Senha",
  aprovado: "Aprovado",
  negado: "Negado",
  solicitado: "Solicitado",
  processado: "Processado",
};


// ─── EmptyState ────────────────────────────────────────────────────────────────

function EmptyState({
  icon: Icon,
  message,
}: {
  icon: typeof ShieldCheck;
  message: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-slate-400 dark:text-slate-600">
      <Icon className="w-10 h-10 mb-3 opacity-40" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="p-6 space-y-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

// ─── Tab: Logs ─────────────────────────────────────────────────────────────────

function AuditoriaRow({ log }: { log: AuditoriaLog }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = log.detalhes && Object.keys(log.detalhes).length > 0;

  return (
    <>
      <tr className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
          {fmtDateTime(log.criado_em)}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-200">
          {log.user_nome}
        </td>
        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-[180px] truncate">
          {log.empresa_nome}
        </td>
        <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 capitalize">
          {ENTIDADES.find((e) => e.value === log.entidade)?.label ??
            log.entidade}
        </td>
        <td className="px-4 py-3">
          <Badge
            className={cn(
              "text-xs font-medium border-0",
              ACAO_COLORS[log.acao] ?? "bg-slate-100 text-slate-700",
            )}
          >
            {ACAO_LABELS[log.acao] ?? log.acao}
          </Badge>
        </td>
        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-[200px] truncate">
          <span className="flex items-center gap-1.5">
            {log.entidade_descricao ?? "—"}
            {log.entidade.includes("laudo") &&
              log.detalhes?.tipo &&
              log.detalhes.tipo !== "avaliacao" && (
                <Badge variant="secondary" className="shrink-0">
                  Reclassificação (legado)
                </Badge>
              )}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          {hasDetails ? (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            >
              {expanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          ) : (
            <span className="text-slate-300 dark:text-slate-600">—</span>
          )}
        </td>
      </tr>
      {expanded && hasDetails && (
        <tr className="bg-slate-50 dark:bg-slate-800/40">
          <td colSpan={7} className="px-6 py-3">
            <pre className="text-xs text-slate-600 dark:text-slate-400 font-mono whitespace-pre-wrap">
              {JSON.stringify(log.detalhes, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function TabLogs({
  isOwner,
  empresas,
}: {
  isOwner: boolean;
  empresas: { id: string; razaoSocial: string }[];
}) {
  const [entidade, setEntidade] = useState("");
  const [acao, setAcao] = useState("");
  const [empresaId, setEmpresaId] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAuditoria({
    entidade: entidade || undefined,
    acao: acao || undefined,
    empresaId: empresaId || undefined,
    dataInicio: dataInicio || undefined,
    dataFim: dataFim || undefined,
    page,
  });

  const logs = data?.data ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 50;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const hasFilters = entidade || acao || empresaId || dataInicio || dataFim;

  function clearFilters() {
    setEntidade("");
    setAcao("");
    setEmpresaId("");
    setDataInicio("");
    setDataFim("");
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="w-4 h-4" /> Filtros
            </CardTitle>
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-slate-500 hover:text-slate-700 h-7 gap-1"
              >
                <X className="w-3 h-3" /> Limpar
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Entidade</Label>
              <Select
                value={entidade}
                onValueChange={(v) => {
                  setEntidade(v === "__all__" ? "" : v);
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  {ENTIDADES.map((e) => (
                    <SelectItem key={e.value} value={e.value}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Ação</Label>
              <Select
                value={acao}
                onValueChange={(v) => {
                  setAcao(v === "__all__" ? "" : v);
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  {ACOES.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isOwner && (
              <div className="space-y-1">
                <Label className="text-xs">Empresa</Label>
                <Select
                  value={empresaId}
                  onValueChange={(v) => {
                    setEmpresaId(v === "__all__" ? "" : v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas</SelectItem>
                    {empresas.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Data início</Label>
              <Input
                type="date"
                className="h-9"
                value={dataInicio}
                onChange={(e) => {
                  setDataInicio(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data fim</Label>
              <Input
                type="date"
                className="h-9"
                value={dataFim}
                onChange={(e) => {
                  setDataFim(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingSkeleton />
          ) : logs.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              message="Nenhum registro encontrado"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    {[
                      "Data/Hora",
                      "Usuário",
                      "Empresa",
                      "Entidade",
                      "Ação",
                      "Descrição",
                      "+",
                    ].map((h, i) => (
                      <th
                        key={i}
                        className={cn(
                          "px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide",
                          i === 6 && "text-center",
                        )}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <AuditoriaRow key={log.id} log={log} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isLoading && total > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-slate-800">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {total} registro{total !== 1 ? "s" : ""} no total
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600 dark:text-slate-400 min-w-[80px] text-center">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ─── Main Page ─────────────────────────────────────────────────────────────────

export function Auditoria() {
  const { profile } = useAuth();
  const roles = profile?.roles ?? [];
  const isOwner = roles.includes("owner");

  const { data: empresasData } = useEmpresas();
  const empresas = (empresasData ?? []).map((e) => ({
    id: e.id,
    razaoSocial: e.razaoSocial,
  }));

  return (
    <Shell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              Auditoria
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Histórico de ações do sistema
            </p>
          </div>
        </div>

        <Tabs defaultValue="logs">
          <TabsList className="mb-2">
            <TabsTrigger value="logs" className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Logs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="logs">
            <TabLogs isOwner={isOwner} empresas={empresas} />
          </TabsContent>
        </Tabs>
      </div>
    </Shell>
  );
}
